import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, VariantUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [body]
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [loading]
 * @property {HTMLButtonElement} [closeButton]
 */

/**
 * Modal that lists similar products filtered by size (same collection first).
 * @extends {DialogComponent}
 */
class SimilarProductsDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {string} */
  #activeSize = '';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    this.#fetchController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-similar-products-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.openFromTrigger(trigger);
    }
  };

  /**
   * @param {Event} event
   */
  #onCartAdd = (event) => {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const detail = /** @type {{ data?: { didError?: boolean } }} */ (event).detail;
    if (detail?.data?.didError) return;

    const source = event.target;
    if (source instanceof Node && this.contains(source)) {
      this.closeDialog();
    }
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const productId = event.detail.data.productId;
    if (!productId) return;

    const button = document.querySelector(
      `product-form-component[data-product-id="${productId}"] [data-similar-products-trigger]`
    );
    if (!(button instanceof HTMLButtonElement)) return;

    const html = event.detail.data.html;
    const newButton =
      html instanceof Document
        ? html.querySelector(
            `product-form-component[data-product-id="${productId}"] [data-similar-products-trigger], [data-similar-products-trigger]`
          )
        : null;

    if (newButton instanceof HTMLButtonElement) {
      button.hidden = newButton.hasAttribute('hidden');
      button.dataset.size = newButton.dataset.size || '';
      button.dataset.sizeOptionPosition = newButton.dataset.sizeOptionPosition || '0';
      button.dataset.productUrl = newButton.dataset.productUrl || button.dataset.productUrl || '';
      return;
    }

    const variant = event.detail.resource;
    const available = variant?.available === true;
    button.hidden = available;

    const position = Number(button.dataset.sizeOptionPosition || '0');
    if (!variant || !position) {
      if (!available) button.dataset.size = '';
      return;
    }

    const optionKey = /** @type {'option1' | 'option2' | 'option3'} */ (`option${position}`);
    const sizeValue = variant[optionKey];
    button.dataset.size = typeof sizeValue === 'string' ? sizeValue.trim() : '';
  };

  /**
   * @param {HTMLButtonElement} trigger
   */
  async openFromTrigger(trigger) {
    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;

    this.#activeSize = this.#normalizeSize(trigger.dataset.size || '');
    this.#showLoading();
    this.showDialog();

    const title = this.querySelector('#similar-products-modal-title');
    if (title instanceof HTMLElement) {
      requestAnimationFrame(() => {
        title.focus();
      });
    }

    await this.#loadProducts(productUrl);
  }

  #showLoading() {
    const { body, count } = this.refs;
    if (count) count.textContent = '';
    if (!body) return;

    const loading = document.createElement('p');
    loading.className = 'similar-products-modal__loading';
    loading.textContent =
      this.dataset.loadingLabel || this.refs.loading?.textContent?.trim() || '…';
    body.replaceChildren(loading);
  }

  /**
   * @param {string} productUrl
   */
  async #loadProducts(productUrl) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const requestId = this.#fetchController;

    const sectionId = this.dataset.sectionId || 'similar-products-by-size';
    const url = new URL(productUrl, window.location.origin);

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (requestId !== this.#fetchController) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('[data-similar-products-list]');
      const cards = list ? [...list.querySelectorAll('[data-similar-product]')] : [];
      this.#renderFilteredCards(/** @type {HTMLElement[]} */ (cards));
    } catch (error) {
      if (requestId !== this.#fetchController) return;
      console.error('Similar products failed to load:', error);
      this.#renderEmpty();
    }
  }

  /**
   * @param {HTMLElement[]} cards
   */
  #renderFilteredCards(cards) {
    if (cards.length === 0) {
      this.#renderEmpty();
      return;
    }

    const sizeMatches = cards.filter((card) => this.#matchesSize(card, this.#activeSize));
    let selected = sizeMatches;

    // Prefer same collection + size; if none, same size elsewhere; if still none, show collection products.
    const primarySizeMatches = sizeMatches.filter((card) => card.dataset.primary === 'true');
    if (primarySizeMatches.length > 0) {
      selected = primarySizeMatches;
    } else if (sizeMatches.length === 0) {
      const primaryAny = cards.filter((card) => card.dataset.primary === 'true');
      selected = primaryAny.length > 0 ? primaryAny : cards;
    }

    const { body, count } = this.refs;
    if (!body) return;

    const list = document.createElement('ul');
    list.className = 'similar-products-recs__list list-unstyled';
    list.setAttribute('role', 'list');

    for (const card of selected) {
      const liveCard = /** @type {HTMLElement} */ (document.importNode(card, true));
      liveCard.hidden = false;
      liveCard.removeAttribute('hidden');
      list.append(liveCard);
    }

    body.replaceChildren(list);
    if (count) count.textContent = this.#formatItemCount(selected.length);
  }

  #renderEmpty() {
    const { body, count } = this.refs;
    if (count) count.textContent = this.#formatItemCount(0);
    if (!body) return;

    const empty = document.createElement('p');
    empty.className = 'similar-products-modal__empty';
    empty.textContent = this.dataset.emptyLabel || '';
    body.replaceChildren(empty);
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatItemCount(count) {
    const template = count === 1 ? this.dataset.itemsOne : this.dataset.itemsOther;
    if (!template) return String(count);
    return template.replace('COUNT', String(count));
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeSize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();
  }

  /**
   * @param {HTMLElement} card
   * @param {string} size
   * @returns {boolean}
   */
  #matchesSize(card, size) {
    const raw = card.dataset.availableSizes || '';
    const sizes = raw
      .split('||')
      .map((value) => this.#normalizeSize(value))
      .filter((value) => value.length > 0);

    if (sizes.length === 0) return false;
    if (!size) return true;
    if (sizes.includes('__any__')) return true;

    return sizes.includes(size);
  }
}

if (!customElements.get('similar-products-dialog')) {
  customElements.define('similar-products-dialog', SimilarProductsDialog);
}
